// src/data/battleSkills.js
// Skills de combat démoniaques — système pierre/feuille/ciseaux enrichi
// Chaque skill a un type, des dégâts de base, des boosts stats, et des counters

// ── Types et matchups ─────────────────────────────────────────────────────────
// ATTAQUE bat DÉFENSE, DÉFENSE bat EFFET, EFFET bat ATTAQUE
// PIÈGE est neutre mais surprend les ATTAQUES
// LECTURE bat PIÈGE
export const TYPE_MATCHUPS = {
  attaque:  { beats: "defense",  losesTo: "effet",   beats2: null },
  defense:  { beats: "effet",    losesTo: "attaque",  beats2: null },
  effet:    { beats: "attaque",  losesTo: "defense",  beats2: null },
  piege:    { beats: "attaque",  losesTo: "lecture",  beats2: null },
  lecture:  { beats: "piege",    losesTo: "effet",    beats2: null },
};

export const TYPE_META = {
  attaque: { label: "ATTAQUE",  color: "#c0392b", icon: "⚡" },
  defense: { label: "DÉFENSE",  color: "#5b8dd9", icon: "🛡️" },
  effet:   { label: "EFFET",    color: "#b07fe8", icon: "🌀" },
  piege:   { label: "PIÈGE",    color: "#d4a017", icon: "🪤" },
  lecture: { label: "LECTURE",  color: "#4a9e6b", icon: "👁️" },
};

// ── Skills complets ───────────────────────────────────────────────────────────
// damage: dégâts de base (0-100)
// statBoost: quelle stat amplifie ces dégâts (multiplicateur)
// counter: skill qui neutralise celui-ci
// description: texte affiché au joueur

export const ALL_SKILLS = [

  // ── ATTAQUES ──────────────────────────────────────────────────────────────
  {
    id: "smash_direct",
    name: "SMASH DIRECT",
    jp: "強打",
    icon: "💥",
    type: "attaque",
    damage: 75,
    statBoost: "aggression",
    desc: "Coup frontal à pleine puissance. Pas de subtilité. Beaucoup de dégâts.",
    lore: "Le démon frappe. Le filet tremble.",
    perkReq: null,
  },
  {
    id: "loop_top",
    name: "LOOP TOPSPIN",
    jp: "ループ",
    icon: "🌊",
    type: "attaque",
    damage: 65,
    statBoost: "creativity",
    desc: "Lift puissant avec rotation. Dévie les retours défensifs.",
    lore: "La balle plonge comme une sentence.",
    perkReq: null,
  },
  {
    id: "contre_attaque",
    name: "CONTRE-ATTAQUE",
    jp: "反撃",
    icon: "⚔️",
    type: "attaque",
    damage: 80,
    statBoost: "adaptation",
    desc: "Retourne la puissance adverse. Plus fort si l'ennemi attaque.",
    lore: "Utilise la rage de l'autre comme combustible.",
    perkReq: null,
    bonus: "Si l'adversaire joue ATTAQUE, +40 dégâts",
  },
  {
    id: "attaque_eclair",
    name: "ATTAQUE ÉCLAIR",
    jp: "電撃打",
    icon: "⚡",
    type: "attaque",
    damage: 55,
    statBoost: "risk",
    desc: "Vitesse pure. L'adversaire n'a pas le temps de répondre.",
    lore: "Avant même que l'œil le voie, c'est fini.",
    perkReq: null,
    bonus: "Ignore partiellement les DÉFENSES",
  },

  // ── DÉFENSES ─────────────────────────────────────────────────────────────
  {
    id: "bloc_plat",
    name: "BLOC PLAT",
    jp: "ブロック",
    icon: "🧱",
    type: "defense",
    damage: 20,
    statBoost: "control",
    desc: "Renvoi plat et court. Annule les effets adverses. Contre-tempo.",
    lore: "Le démon absorbe. Et renvoie exactement ce qu'il a reçu.",
    perkReq: null,
  },
  {
    id: "chop_defensif",
    name: "CHOP DÉFENSIF",
    jp: "チョップ",
    icon: "🛡️",
    type: "defense",
    damage: 30,
    statBoost: "patience",
    desc: "Balle coupée longue. Réduit les dégâts reçus de 40%.",
    lore: "La patience du démon est une armure.",
    perkReq: null,
    bonus: "Réduit les dégâts adverses de 40%",
  },
  {
    id: "amortie",
    name: "AMORTIE",
    jp: "ショート",
    icon: "🪃",
    type: "defense",
    damage: 25,
    statBoost: "control",
    desc: "Balle courte inattendue. Perturbe le rythme adverse.",
    lore: "La balle meurt avant d'avoir existé.",
    perkReq: null,
    bonus: "Si l'adversaire joue ATTAQUE, contre-temps +30",
  },

  // ── EFFETS ────────────────────────────────────────────────────────────────
  {
    id: "service_coupe",
    name: "SERVICE COUPÉ",
    jp: "カットサービス",
    icon: "🌀",
    type: "effet",
    damage: 50,
    statBoost: "chaos",
    desc: "Service avec rotation cachée. L'adversaire revient dans le filet.",
    lore: "Le secret est dans le poignet. Et dans le mensonge.",
    perkReq: null,
  },
  {
    id: "service_lateral",
    name: "SERVICE LATÉRAL",
    jp: "横回転",
    icon: "↩️",
    type: "effet",
    damage: 55,
    statBoost: "chaos",
    desc: "Rotation latérale invisible. Le retour part à 90 degrés.",
    lore: "La physique est une arme. Le démon la maîtrise.",
    perkReq: null,
  },
  {
    id: "variation_rythme",
    name: "VARIATION DE RYTHME",
    jp: "リズム変化",
    icon: "🎛️",
    type: "effet",
    damage: 45,
    statBoost: "creativity",
    desc: "Alterne vitesses et longueurs. Désynchronise l'adversaire.",
    lore: "Le chaos est une mélodie. Il en joue le chef.",
    perkReq: null,
    bonus: "Si l'adversaire joue DÉFENSE, +35 dégâts",
  },
  {
    id: "picot_inverseur",
    name: "PICOT INVERSEUR",
    jp: "ピコット",
    icon: "🐸",
    type: "effet",
    damage: 60,
    statBoost: "chaos",
    desc: "Renvoie les effets adverses à l'envers. Cause de la confusion totale.",
    lore: "Ce que tu envoies, le démon te le retourne amplifié.",
    perkReq: "picot",
  },
  {
    id: "multiball_pressure",
    name: "PRESSION MULTI-BALLES",
    jp: "連打",
    icon: "🔴",
    type: "effet",
    damage: 40,
    statBoost: "discipline",
    desc: "Série de frappes rapides sur le même côté. Épuise l'adversaire.",
    lore: "Pas de repos. Pas de merci. Encore.",
    perkReq: null,
    bonus: "Si joué 2 fois de suite, +50% dégâts",
  },

  // ── PIÈGES ────────────────────────────────────────────────────────────────
  {
    id: "fausse_attaque",
    name: "FAUSSE ATTAQUE",
    jp: "フェイント",
    icon: "🎭",
    type: "piege",
    damage: 60,
    statBoost: "ego",
    desc: "Simule une attaque puis choisit le côté opposé. Bat les défenses préparées.",
    lore: "Le démon n'est jamais là où on l'attend.",
    perkReq: null,
    bonus: "Ignore les DÉFENSES si l'adversaire a joué DÉFENSE au tour précédent",
  },
  {
    id: "appat_court",
    name: "APPÂT COURT",
    jp: "おとり",
    icon: "🪤",
    type: "piege",
    damage: 65,
    statBoost: "adaptation",
    desc: "Balle courte qui attire l'adversaire en avant, puis smash dans le fond.",
    lore: "La proie s'approche. Le démon sourit.",
    perkReq: null,
  },

  // ── LECTURES ─────────────────────────────────────────────────────────────
  {
    id: "lecture_effets",
    name: "LECTURE DES EFFETS",
    jp: "回転読み",
    icon: "👁️",
    type: "lecture",
    damage: 35,
    statBoost: "adaptation",
    desc: "Anticipe la rotation adverse et la neutralise parfaitement.",
    lore: "Le démon voit ce que l'autre cache.",
    perkReq: null,
    bonus: "Neutralise totalement les skills EFFET",
  },
  {
    id: "analyse_tactique",
    name: "ANALYSE TACTIQUE",
    jp: "戦術分析",
    icon: "🔍",
    type: "lecture",
    damage: 40,
    statBoost: "discipline",
    desc: "Observe le pattern adverse et prédit le prochain coup. +damage si correct.",
    lore: "La bataille est gagnée avant d'être jouée.",
    perkReq: null,
    bonus: "Si l'adversaire répète un type de skill, +60 dégâts",
  },
  {
    id: "contre_mental",
    name: "CONTRE MENTAL",
    jp: "精神的反撃",
    icon: "🧠",
    type: "lecture",
    damage: 50,
    statBoost: "ego",
    desc: "Exploite le tilt adverse. Plus efficace si l'ennemi a perdu des points.",
    lore: "Le démon ne combat pas le corps. Il combat l'esprit.",
    perkReq: null,
    bonus: "Si l'adversaire est en dessous de 30 HP, +50 dégâts",
  },
];

// ── Assign skills à un Oni selon ses stats et perks ───────────────────────────
// Retourne les 5 skills les plus adaptés à cet Oni
export function getOniSkills(oni, stats, perks = []) {
  if (!stats) return ALL_SKILLS.slice(0, 5);

  const scored = ALL_SKILLS
    .filter(s => !s.perkReq || perks.includes(s.perkReq))
    .map(s => {
      const statVal = stats[s.statBoost] || 50;
      let score = statVal;
      // Bonus selon le profil de l'Oni
      if (s.type === "attaque" && stats.aggression > 60) score += 20;
      if (s.type === "defense" && stats.patience > 60)   score += 20;
      if (s.type === "effet"   && stats.chaos > 60)      score += 20;
      if (s.type === "lecture" && stats.adaptation > 60) score += 20;
      if (s.type === "piege"   && stats.creativity > 60) score += 20;
      // Petite variance pour que ce soit pas toujours les mêmes
      score += Math.sin(oni.id.charCodeAt(0) + s.id.charCodeAt(0)) * 10;
      return { ...s, score };
    })
    .sort((a, b) => b.score - a.score);

  // Garantit au moins un skill de chaque type parmi attaque/defense/effet
  const result = [];
  const types  = ["attaque", "defense", "effet"];
  types.forEach(t => {
    const best = scored.find(s => s.type === t && !result.includes(s));
    if (best) result.push(best);
  });
  // Complète avec les meilleurs restants jusqu'à 6 skills
  scored.forEach(s => {
    if (result.length < 6 && !result.includes(s)) result.push(s);
  });

  return result.slice(0, 6);
}

// ── Calcule le résultat d'un échange de 3 skills ──────────────────────────────
// p1Skills et p2Skills : tableaux de 3 skill IDs
// Retourne { p1Points, p2Points, rounds: [{p1Skill, p2Skill, winner, damage1, damage2}] }
export function resolveBattle(p1Skills, p2Skills, p1Stats, p2Stats) {
  const rounds = [];
  let p1HP = 100, p2HP = 100;

  for (let i = 0; i < 3; i++) {
    const s1 = ALL_SKILLS.find(s => s.id === p1Skills[i]);
    const s2 = ALL_SKILLS.find(s => s.id === p2Skills[i]);
    if (!s1 || !s2) continue;

    const matchup = TYPE_MATCHUPS[s1.type];
    const matchup2 = TYPE_MATCHUPS[s2.type];

    // Calcul des dégâts avec boost stats
    const statMult1 = ((p1Stats?.[s1.statBoost] || 50) / 100) + 0.5; // 0.5 à 1.5
    const statMult2 = ((p2Stats?.[s2.statBoost] || 50) / 100) + 0.5;
    let dmg1 = Math.round(s1.damage * statMult1);
    let dmg2 = Math.round(s2.damage * statMult2);

    // Résolution du matchup
    let winner = "draw";
    if (matchup.beats === s2.type) {
      // s1 bat s2
      dmg1 = Math.round(dmg1 * 1.5);
      dmg2 = Math.round(dmg2 * 0.5);
      winner = "p1";
    } else if (matchup.losesTo === s2.type) {
      // s2 bat s1
      dmg1 = Math.round(dmg1 * 0.5);
      dmg2 = Math.round(dmg2 * 1.5);
      winner = "p2";
    }
    // Cas piège
    if (s1.type === "piege" && s2.type === "attaque") { dmg1 = Math.round(dmg1*1.4); winner = "p1"; }
    if (s2.type === "piege" && s1.type === "attaque") { dmg2 = Math.round(dmg2*1.4); winner = "p2"; }

    p1HP -= dmg2;
    p2HP -= dmg1;

    rounds.push({
      p1Skill: s1, p2Skill: s2,
      winner, dmg1, dmg2,
      p1HPAfter: Math.max(0, p1HP),
      p2HPAfter: Math.max(0, p2HP),
    });
  }

  return {
    rounds,
    p1HP: Math.max(0, p1HP),
    p2HP: Math.max(0, p2HP),
    winner: p1HP > p2HP ? "p1" : p2HP > p1HP ? "p2" : "draw",
  };
}
